'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useWholesalerBranding } from '@/context/WholesalerBrandingContext';
import '../../auth/auth-design.css';

type BrandMode = 'in' | 'up' | 'done';

interface AuthSplitLayoutProps {
  children: React.ReactNode;
  mode?: BrandMode;
  stepIndex?: string;
  selectedAccount?: string | null;
  toast?: string;
}

const BRAND_LINES: Record<BrandMode, string[]> = {
  in:   ['Build, sell, and', 'extend modern', 'commerce — from', 'one workspace.'],
  up:   ['Build Your Market Palace.', 'Develop For CresivoX.'],
  done: ['Welcome to the', 'ecosystem.', "Let's build", 'something.'],
};

export function AuthSplitLayout({
  children,
  mode = 'in',
  stepIndex,
  selectedAccount,
  toast,
}: AuthSplitLayoutProps) {
  const pathname = usePathname();
  const isSignup = pathname?.includes('sign-up') || pathname?.includes('register');

  const lines = BRAND_LINES[mode];

  const ECOSYSTEM = [
    ['01', 'Storefronts', 'Launch in a click'],
    ['02', 'Marketplace', 'Resell, fulfil, ship'],
    ['03', 'Wholesale', 'Supply at scale'],
    ['04', 'Developer API', 'Plugins & themes'],
  ];

  return (
    <div className="auth-root" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* top bar */}
      <header style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: 'clamp(18px,3vh,28px) clamp(20px,5vw,48px)',
        position: 'relative', zIndex: 2,
      }}>
        <Brandmark />
        <div style={{ fontSize: 13.5, color: 'var(--muted)' }}>
          {isSignup
            ? <span>Already a member?{' '}<Link href="/auth/sign-in-1" className="auth-linklike" style={{ fontSize: 13.5 }}>Sign in</Link></span>
            : <span>New here?{' '}<Link href="/auth/sign-up-1" className="auth-linklike" style={{ fontSize: 13.5 }}>Create account</Link></span>}
        </div>
      </header>

      {/* body */}
      <div style={{ flex: 1, display: 'flex', width: '100%' }}>
        {/* form column */}
        <main style={{
          flex: '1 1 52%',
          display: 'flex', justifyContent: 'center', alignItems: 'flex-start',
          padding: 'clamp(16px,4vh,56px) clamp(20px,5vw,48px) 64px',
          overflow: 'auto',
        }}>
          <div style={{ width: '100%', maxWidth: 452 }}>
            {/* mobile brand lines — hidden on lg where aside panel shows them */}
            <div className="lg:hidden" style={{ marginBottom: 28, padding: '18px 20px', borderRadius: 14, background: '#16140F' }}>
              <p style={{ margin: 0, fontFamily: '"Newsreader", Georgia, serif', fontSize: 'clamp(20px,5vw,28px)', lineHeight: 1.15, color: '#FCFBF8', letterSpacing: '-0.01em' }}>
                {lines.map((l, i) => (
                  <span key={i} style={{ display: 'block', fontStyle: i === lines.length - 1 ? 'italic' : 'normal' }}>{l}</span>
                ))}
              </p>
            </div>
            {toast && (
              <div className="auth-toast">
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--accent)', flex: '0 0 auto' }} />
                {toast}
              </div>
            )}
            <div className="auth-screen-enter">
              {children}
            </div>
            <footer style={{ marginTop: 40, paddingTop: 18, borderTop: '1px solid var(--line)', display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--faint)' }}>
              <FooterCopyright />
              <span style={{ display: 'flex', gap: 16 }}>
                <button className="auth-linklike" style={{ fontSize: 12, color: 'var(--faint)', textDecorationColor: 'transparent' }}>Privacy</button>
                <button className="auth-linklike" style={{ fontSize: 12, color: 'var(--faint)', textDecorationColor: 'transparent' }}>Terms</button>
              </span>
            </footer>
          </div>
        </main>

        {/* brand canvas */}
        <aside style={{
          flex: '1 1 48%',
          background: '#16140F',
          color: '#FCFBF8',
          padding: 'clamp(32px,4vw,64px)',
          justifyContent: 'space-between',
          position: 'relative', overflow: 'hidden', minHeight: 0,
        }} className="hidden lg:flex lg:flex-col">
          {/* faint grid */}
          <div style={{
            position: 'absolute', inset: 0, opacity: 0.06, pointerEvents: 'none',
            backgroundImage: 'linear-gradient(#FCFBF8 1px, transparent 1px), linear-gradient(90deg, #FCFBF8 1px, transparent 1px)',
            backgroundSize: '64px 64px',
          }} />

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative' }}>
            <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#FCFBF8', opacity: 0.6 }}>
              The commerce ecosystem
            </span>
            <span style={{ fontFamily: '"Newsreader", Georgia, serif', fontSize: 15, color: '#FCFBF8', opacity: 0.55 }}>{stepIndex || '—'}</span>
          </div>

          <div style={{ position: 'relative' }}>
            <h2 style={{
              fontFamily: '"Newsreader", Georgia, serif',
              fontWeight: 400,
              fontSize: 'clamp(34px,4.4vw,60px)',
              lineHeight: 1.05,
              letterSpacing: '-0.015em',
              margin: 0,
              color: '#FCFBF8',
            }}>
              {lines.map((l, i) => (
                <span key={i} style={{ display: 'block', fontStyle: i === lines.length - 1 ? 'italic' : 'normal', opacity: i === 0 ? 1 : 0.92 }}>{l}</span>
              ))}
            </h2>
          </div>

          <div style={{ position: 'relative' }}>
            <div style={{ display: 'grid', gap: 0 }}>
              {ECOSYSTEM.map(([n, title, sub], i) => {
                const activeRow =
                  (selectedAccount === 'reseller' && i === 1) ||
                  (selectedAccount === 'wholesaler' && i === 2) ||
                  (selectedAccount === 'developer' && i === 3);
                return (
                  <div key={n} style={{
                    display: 'flex', alignItems: 'center', gap: 16, padding: '13px 0',
                    borderTop: i === 0 ? '1px solid rgba(252,251,248,0.18)' : 'none',
                    borderBottom: '1px solid rgba(252,251,248,0.18)',
                    color: '#FCFBF8',
                    opacity: selectedAccount ? (activeRow ? 1 : 0.45) : 0.85,
                    transition: 'opacity .3s ease',
                  }}>
                    <span style={{ fontFamily: '"Newsreader", Georgia, serif', fontSize: 14, opacity: 0.6, width: 22, color: '#FCFBF8' }}>{n}</span>
                    <span style={{ flex: 1, fontSize: 15.5, fontWeight: 500, color: '#FCFBF8' }}>{title}</span>
                    <span style={{ fontSize: 12.5, opacity: 0.55, letterSpacing: '0.02em', color: '#FCFBF8' }}>{sub}</span>
                    {activeRow && <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#FCFBF8' }} />}
                  </div>
                );
              })}
            </div>
            <p style={{ margin: '22px 0 0', fontSize: 12.5, opacity: 0.5, letterSpacing: '0.02em', color: '#FCFBF8' }}>
              Trusted by independent makers and supply teams in 40+ markets.
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}

/* ---- Footer Copyright ---- */
function FooterCopyright() {
  const { isWhitelabel, brandName } = useWholesalerBranding();
  return <span>© {new Date().getFullYear()} {isWhitelabel ? brandName : 'cresivox'}</span>;
}

/* ---- Brandmark ---- */
function Brandmark() {
  const { isWhitelabel, branding, brandName } = useWholesalerBranding();

  if (isWhitelabel && branding?.logo_url) {
    return <img src={branding.logo_url} alt={brandName} style={{ height: 32, objectFit: 'contain' }} />;
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
      {!isWhitelabel && (
        <span style={{
          width: 24, height: 24, borderRadius: 5,
          background: 'var(--ink)', color: 'var(--card)',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: 'var(--serif)', fontSize: 15, fontWeight: 500, lineHeight: 1,
          flex: '0 0 auto',
        }}>C</span>
      )}
      <span style={{ fontFamily: 'var(--sans)', fontWeight: 600, fontSize: 16.5, letterSpacing: '-0.01em', color: 'var(--ink)' }}>
        {brandName}
      </span>
    </div>
  );
}

/* ---- Stepper ---- */
export function AuthStepper({ steps, current }: { steps: string[]; current: number }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
      {steps.map((s, i) => {
        const done = i < current, active = i === current;
        return (
          <div key={s} style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span className="auth-serif" style={{
                fontSize: 14,
                color: active ? 'var(--ink)' : done ? 'var(--ink-2)' : 'var(--faint)',
                width: 20, textAlign: 'center',
              }}>{done ? '✓' : String(i + 1).padStart(2, '0')}</span>
              <span style={{
                fontSize: 12.5, fontWeight: active ? 600 : 500,
                color: active ? 'var(--ink)' : done ? 'var(--ink-2)' : 'var(--faint)',
                letterSpacing: '0.01em',
              }}>{s}</span>
            </div>
            {i < steps.length - 1 && (
              <span style={{ width: 26, height: 1, background: 'var(--line-2)', margin: '0 12px' }} />
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ---- AuthField ---- */
export function AuthField({ label, error, hint, id, ...props }: {
  label?: string; error?: string; hint?: string; id?: string;
  [k: string]: any;
}) {
  return (
    <div>
      {label && <label className="auth-field-label" htmlFor={id}>{label}</label>}
      <input id={id} className={'auth-field' + (error ? ' has-error' : '')} {...props} />
      {error
        ? <div className="auth-err"><DotIcon />{error}</div>
        : hint ? <div style={{ marginTop: 7, fontSize: 12.5, color: 'var(--muted)' }}>{hint}</div>
        : null}
    </div>
  );
}

/* ---- AuthPasswordField ---- */
export function AuthPasswordField({ label, error, hint, id, value, onChange, autoComplete, placeholder }: {
  label?: string; error?: string; hint?: string; id?: string;
  value?: string; onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  autoComplete?: string; placeholder?: string;
}) {
  const [show, setShow] = useState(false);
  return (
    <div>
      {label && <label className="auth-field-label" htmlFor={id}>{label}</label>}
      <div style={{ position: 'relative' }}>
        <input
          id={id}
          className={'auth-field' + (error ? ' has-error' : '')}
          type={show ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          placeholder={placeholder || '••••••••'}
          autoComplete={autoComplete}
          style={{ paddingRight: 44 }}
        />
        <button
          type="button"
          onClick={() => setShow(s => !s)}
          aria-label={show ? 'Hide password' : 'Show password'}
          style={{
            position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
            background: 'none', border: 'none', cursor: 'pointer', padding: 8,
            color: 'var(--muted)', display: 'flex',
          }}
        >
          {show ? <EyeOff /> : <Eye />}
        </button>
      </div>
      {error
        ? <div className="auth-err"><DotIcon />{error}</div>
        : hint ? <div style={{ marginTop: 7, fontSize: 12.5, color: 'var(--muted)' }}>{hint}</div>
        : null}
    </div>
  );
}

/* ---- AuthButton ---- */
export function AuthButton({ loading, children, ghost, style, ...props }: {
  loading?: boolean; children: React.ReactNode; ghost?: boolean; style?: React.CSSProperties;
  [k: string]: any;
}) {
  return (
    <button
      className={'auth-btn ' + (ghost ? 'auth-btn-ghost' : 'auth-btn-primary')}
      disabled={loading || props.disabled}
      style={style}
      {...props}
    >
      {loading ? <><AuthSpinner />Please wait…</> : children}
    </button>
  );
}

/* ---- OrRule ---- */
export function AuthOrRule({ children = 'or' }: { children?: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, color: 'var(--faint)' }}>
      <span style={{ flex: 1, height: 1, background: 'var(--line)' }} />
      <span style={{ fontSize: 12, letterSpacing: '0.08em', textTransform: 'uppercase' }}>{children}</span>
      <span style={{ flex: 1, height: 1, background: 'var(--line)' }} />
    </div>
  );
}

/* ---- GoogleButton ---- */
export function AuthGoogleButton({ onClick, label = 'Continue with Google' }: { onClick?: () => void; label?: string }) {
  return (
    <button type="button" className="auth-btn auth-btn-ghost" onClick={onClick}>
      <GoogleG />{label}
    </button>
  );
}

/* ---- FacebookButton ---- */
export function AuthFacebookButton({ onClick, label = 'Continue with Facebook' }: { onClick?: () => void; label?: string }) {
  return (
    <button type="button" className="auth-btn auth-btn-ghost" onClick={onClick}>
      <FacebookF />{label}
    </button>
  );
}

/* ---- StrengthMeter ---- */
export function AuthStrengthMeter({ score, show }: { score: number; show: boolean }) {
  if (!show) return null;
  const labels = ['Too weak', 'Weak', 'Fair', 'Strong', 'Excellent'];
  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ display: 'flex', gap: 5 }}>
        {[0, 1, 2, 3].map(i => (
          <span key={i} className="auth-strength-bar" style={{
            background: i < score ? 'var(--ink)' : 'var(--line)',
          }} />
        ))}
      </div>
      <div style={{ marginTop: 6, fontSize: 12, color: 'var(--muted)' }}>{labels[score]}</div>
    </div>
  );
}

/* ---- icons ---- */
function AuthSpinner() {
  return (
    <svg className="auth-spin" width="16" height="16" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.4" opacity="0.25" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
    </svg>
  );
}
function Eye() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12Z" /><circle cx="12" cy="12" r="3" /></svg>;
}
function EyeOff() {
  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"><path d="M3 3l18 18M10.6 10.6a3 3 0 0 0 4.2 4.2M9.9 5.2A9.6 9.6 0 0 1 12 5c6.4 0 10 7 10 7a17 17 0 0 1-3.2 3.9M6.2 6.2A17 17 0 0 0 2 12s3.6 7 10 7a9.4 9.4 0 0 0 3-.5" /></svg>;
}
function DotIcon() {
  return <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9" /><path d="M12 8v5M12 16h.01" /></svg>;
}
function GoogleG() {
  return (
    <svg width="17" height="17" viewBox="0 0 48 48">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.4 29.3 35 24 35c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 5.1 29.5 3 24 3 12.4 3 3 12.4 3 24s9.4 21 21 21 21-9.4 21-21c0-1.2-.1-2.3-.4-3.5z"/>
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 5.1 29.5 3 24 3 16 3 9.1 7.6 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 45c5.2 0 9.9-2 13.5-5.2l-6.2-5.3C29.2 36 26.7 37 24 37c-5.3 0-9.7-2.6-11.3-7l-6.5 5C9 40.3 15.9 45 24 45z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.2 5.3C41.1 35.9 45 30.6 45 24c0-1.2-.1-2.3-.4-3.5z"/>
    </svg>
  );
}

function FacebookF() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24">
      <path fill="#1877F2" d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07C0 18.1 4.39 23.1 10.13 24v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.7 4.53-4.7 1.31 0 2.68.24 2.68.24v2.97h-1.51c-1.49 0-1.95.93-1.95 1.89v2.26h3.32l-.53 3.49h-2.79V24C19.61 23.1 24 18.1 24 12.07z"/>
    </svg>
  );
}

export { AuthSpinner };
